import { Button, FocusRing } from '@change/ds-core-ui';
import { CloseIcon } from '@change/ds-icons/CloseIcon';
import { SearchIcon } from '@change/ds-icons/SearchIcon';

import { Translate } from '@change-corgi/core-react/i18n';

import { useSearchBox } from './hooks/useSearchBox';
import { useTranslations } from './hooks/useTranslations';

export const SearchBox: React.FC = () => {
	const {
		data: { isLoading, inputRef, inputValue },
		actions: { setQuery, clearQuery, submitSearch },
	} = useSearchBox();

	const {
		data: { placeholder, resetLabel },
	} = useTranslations();

	return (
		<div className="container w-full">
			<div className="mx-auto w-full md:w-8/12">
				<form
					action=""
					// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
					role="search"
					noValidate
					onSubmit={(event) => {
						event.preventDefault();
						event.stopPropagation();
						submitSearch();
						inputRef.current?.blur();
					}}
					onReset={(event) => {
						event.preventDefault();
						event.stopPropagation();
						clearQuery();
						inputRef.current?.focus();
					}}
					className="relative flex w-full items-center justify-center gap-2"
					aria-busy={isLoading}
				>
					<div className="relative w-full">
						<label htmlFor="search-input" className="sr-only">
							<Translate value="corgi.search.search_box.submit" />
						</label>
						<FocusRing>
							<input
								id="search-input"
								ref={inputRef}
								autoComplete="off"
								autoCorrect="off"
								autoCapitalize="off"
								placeholder={placeholder}
								spellCheck={false}
								maxLength={512}
								type="search"
								value={inputValue}
								onChange={(e) => {
									setQuery(e.target.value);
								}}
								className="border-stroke-strong bg-fill-inverse-strong text-size-body-large text-typography-strong placeholder-typography-disabled focus:outline-stroke-focus h-14 w-full rounded-md border border-solid px-10 py-4 focus:outline focus:outline-2 focus:outline-offset-2 [&::-webkit-search-cancel-button]:hidden"
								data-qa="search-input"
							/>
						</FocusRing>
						<SearchIcon className="text-icon-strong absolute top-0 left-3 h-14" />
						{!isLoading && inputValue && <ClearButton resetLabel={resetLabel} />}
					</div>

					<Button
						variant="primary"
						type="submit"
						className="hidden md:block"
						disabled={isLoading}
						data-testid="submit-button"
						data-qa="search-input-submit-button"
					>
						<Translate value="corgi.search.search_box.submit" />
					</Button>
				</form>
			</div>
		</div>
	);
};

const ClearButton: React.FC<{ resetLabel: string }> = ({ resetLabel }) => (
	<div className="absolute top-0 right-0 bottom-0 flex h-14 flex-col justify-center px-2">
		<FocusRing>
			<button
				type="reset"
				aria-label={resetLabel}
				className="bg-fill-inverse-strong text-typography-strong hover:bg-fill-hover focus:bg-fill-hover focus:outline-stroke-focus flex h-10 w-10 items-center justify-center rounded-full focus:outline focus:outline-2 focus:outline-offset-2"
			>
				<CloseIcon aria-hidden="true" />
				<span className="sr-only">
					<Translate value="corgi.search.search_box.reset" />
				</span>
			</button>
		</FocusRing>
	</div>
);
